import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Shield, Mail, Lock, User, Building, Loader2, AlertCircle, Check } from 'lucide-react';
import { authAPI } from '@/lib/api';
import { LOGO_URL } from '@/lib/brand';

const Signup = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        company_name: '',
        password: '',
        confirmPassword: ''
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            setIsLoading(false);
            return;
        }

        if (formData.password.length < 8) {
            setError('Password must be at least 8 characters');
            setIsLoading(false);
            return;
        }

        try {
            const { confirmPassword, ...registerData } = formData;
            const response = await authAPI.register(registerData);
            localStorage.setItem('shirosec_token', response.data.access_token);
            localStorage.setItem('shirosec_user', JSON.stringify(response.data.user));
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.detail || 'Registration failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const benefits = [
        '14-day free trial',
        'No credit card required',
        '3-Layer AI Protection',
        '24/7 autonomous monitoring'
    ];

    return (
        <div className="min-h-screen bg-[#0a0a0f] flex cyber-grid hero-gradient">
            {/* Left Side - Benefits */}
            <div className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-center">
                <div className="max-w-md">
                    {/* Back to Home Link */}
                    <Link 
                        to="/" 
                        className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-400 transition-colors mb-8"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back to Home
                    </Link>

                    <Link to="/" className="inline-flex items-center gap-3 mb-12">
                        <img src={LOGO_URL} alt="Shiro Security" className="w-12 h-12 object-contain" />
                        <div>
                            <span className="text-2xl font-bold gradient-text">SHIRO</span>
                            <span className="text-2xl font-light text-white/80 ml-1">Security</span>
                        </div>
                    </Link>
                    
                    <h2 className="text-4xl font-bold text-white mb-6">
                        Start protecting your business today
                    </h2>
                    <p className="text-xl text-white/60 mb-8">
                        Join thousands of organizations using AI-powered cybersecurity with zero human intervention.
                    </p>
                    
                    <ul className="space-y-4">
                        {benefits.map((benefit, index) => (
                            <li key={index} className="flex items-center gap-3 text-white/80">
                                <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                                    <Check className="w-4 h-4 text-green-400" />
                                </div>
                                {benefit}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Right Side - Form */}
            <div className="flex-1 flex items-center justify-center p-6">
                <div className="w-full max-w-md">
                    {/* Back to Home Link - Mobile */}
                    <div className="lg:hidden mb-4">
                        <Link 
                            to="/" 
                            className="inline-flex items-center gap-2 text-white/60 hover:text-cyan-400 transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Back to Home
                        </Link>
                    </div>

                    <div className="text-center mb-8 lg:hidden">
                        <Link to="/" className="inline-flex items-center gap-3 mb-6">
                            <img src={LOGO_URL} alt="Shiro Security" className="w-12 h-12 object-contain" />
                            <div>
                                <span className="text-2xl font-bold gradient-text">SHIRO</span>
                                <span className="text-2xl font-light text-white/80 ml-1">Security</span>
                            </div>
                        </Link>
                    </div>

                    <Card className="glass-card border-0">
                        <CardContent className="p-8">
                            <h1 className="text-2xl font-bold text-white mb-2">Create your account</h1>
                            <p className="text-white/60 mb-6">Start your free 14-day trial</p>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                {error && (
                                    <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                        {error}
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="full_name" className="text-white/80">Full Name</Label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                                        <Input
                                            id="full_name"
                                            name="full_name"
                                            type="text"
                                            value={formData.full_name}
                                            onChange={handleChange}
                                            placeholder="John Doe"
                                            className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-cyan-500"
                                            required
                                            data-testid="signup-name"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-white/80">Work Email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="you@company.com"
                                            className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-cyan-500"
                                            required
                                            data-testid="signup-email"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="company_name" className="text-white/80">Company Name</Label>
                                    <div className="relative">
                                        <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                                        <Input
                                            id="company_name"
                                            name="company_name"
                                            type="text"
                                            value={formData.company_name}
                                            onChange={handleChange}
                                            placeholder="Your Company"
                                            className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-cyan-500"
                                            required
                                            data-testid="signup-company"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="password" className="text-white/80">Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                                        <Input
                                            id="password"
                                            name="password"
                                            type="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            placeholder="At least 8 characters"
                                            className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-cyan-500"
                                            required
                                            data-testid="signup-password"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword" className="text-white/80">Confirm Password</Label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                                        <Input
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            type="password"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            placeholder="Confirm your password"
                                            className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-cyan-500"
                                            required
                                            data-testid="signup-confirm-password"
                                        />
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white py-6"
                                    data-testid="signup-submit"
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Creating account...
                                        </>
                                    ) : (
                                        'Start Free Trial'
                                    )}
                                </Button>

                                <p className="text-xs text-white/40 text-center">
                                    By signing up, you agree to our{' '}
                                    <a href="#" className="text-cyan-400 hover:underline">Terms of Service</a>{' '}
                                    and{' '}
                                    <a href="#" className="text-cyan-400 hover:underline">Privacy Policy</a>
                                </p>
                            </form>

                            <div className="mt-6 text-center text-white/60">
                                Already have an account?{' '}
                                <Link to="/login" className="text-cyan-400 hover:text-cyan-300 font-medium">
                                    Sign in
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default Signup;
